/*
 * Elexa video helper (D2 C8), shared by the video slider, benefits list and product-page video strip.
 *
 * Elexa.video.load(video)      copy data-src -> src on the <video> and its <source>s, then load()
 * Elexa.video.play(video, opt) load if needed and play; resolves true when playback really started,
 *                              false when the browser refused (fails silently). opt.muted forces mute.
 * Elexa.video.pause(video)
 * Elexa.video.slow()           true on 2G / slow-2G / data saver: no autoplay, nothing preloaded
 * Elexa.video.observe(el, { near, enter, leave, threshold })  near = within ~50 px of the viewport,
 *                              enter/leave = at least `threshold` (default 0.5) visible or not
 *
 * <elexa-video autoplay> wraps one <video>: loads it when it comes near the viewport, plays it muted
 * while at least half visible (autoplay attribute) and pauses it when it scrolls away.
 * Markup for a lazy video: snippets/elexa-video-tag.liquid.
 */
(() => {
  window.Elexa = window.Elexa || {};
  if (window.Elexa.video) return;

  const slow = () => {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return Boolean(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')));
  };

  const load = (video) => {
    if (!video || video.dataset.exLoaded === '1') return;
    let changed = false;
    if (video.dataset.src && !video.getAttribute('src')) {
      video.setAttribute('src', video.dataset.src);
      changed = true;
    }
    video.querySelectorAll('source[data-src]').forEach((s) => {
      if (!s.getAttribute('src')) {
        s.setAttribute('src', s.dataset.src);
        changed = true;
      }
    });
    video.dataset.exLoaded = '1';
    if (changed) video.load();
  };

  const play = async (video, opt = {}) => {
    if (!video) return false;
    load(video);
    if (opt.muted) {
      video.muted = true;
      video.setAttribute('muted', '');
    }
    video.playsInline = true;
    try {
      await video.play();
      return true;
    } catch (e) {
      return false;
    }
  };

  const pause = (video) => {
    if (video && !video.paused) video.pause();
  };

  const observe = (el, { near, enter, leave, threshold = 0.5 } = {}) => {
    const observers = [];
    if (near) {
      const io = new IntersectionObserver(
        (entries) => entries.forEach((en) => en.isIntersecting && near(en.target)),
        { rootMargin: '50px' }
      );
      io.observe(el);
      observers.push(io);
    }
    if (enter || leave) {
      const io = new IntersectionObserver(
        (entries) =>
          entries.forEach((en) => {
            if (en.isIntersecting && en.intersectionRatio >= threshold) enter && enter(en.target);
            else if (!en.isIntersecting || en.intersectionRatio < threshold) leave && leave(en.target);
          }),
        { threshold: [0, threshold] }
      );
      io.observe(el);
      observers.push(io);
    }
    return () => observers.forEach((o) => o.disconnect());
  };

  window.Elexa.video = { slow, load, play, pause, observe };

  if (!customElements.get('elexa-video')) {
    class ElexaVideo extends HTMLElement {
      connectedCallback() {
        if (this.stop) return;
        const video = this.querySelector('video');
        if (!video) return;
        const wantsAuto = this.hasAttribute('autoplay');
        const auto = wantsAuto && !slow();
        this.stop = observe(this, {
          near: () => {
            if (!(wantsAuto && slow())) load(video);
          },
          enter: () => {
            if (auto && !this.userPaused) play(video, { muted: true });
          },
          leave: () => pause(video),
        });
        if (!video.controls) {
          video.addEventListener('click', () => {
            if (video.paused) {
              this.userPaused = false;
              play(video);
            } else {
              this.userPaused = true;
              pause(video);
            }
          });
        }
      }
      disconnectedCallback() {
        if (this.stop) this.stop();
        this.stop = null;
      }
    }
    customElements.define('elexa-video', ElexaVideo);
  }
})();
